import { useEffect } from 'react'
import { Link } from 'wouter'
import { getBodyById, getFactionById } from '../lore'
import { Markdown } from '../components/Markdown'
import { BodyExtras, bodyChapters } from '../components/BodyExtras'
import { LorebookShell, LoreSection, type Chapter } from '../components/LorebookShell'

const typeLabel: Record<string, string> = {
  star: 'Star',
  molten: 'Molten core world',
  rocky: 'Rocky planet',
  terrestrial: 'Terrestrial',
  'gas-giant': 'Gas giant',
  frozen: 'Frozen world',
  dwarf: 'Dwarf planet',
  debris: 'Debris field',
  'asteroid-belt': 'Asteroid belt',
}

/** Full detail page for a body or faction, by id. */
export function LorebookDetail({ id }: { id: string }) {
  const body = getBodyById(id)
  const faction = getFactionById(id)

  // Jump to the section named in the URL hash (#fauna-mournwhisper, #leadership, ...).
  // Two cases:
  //  - cross-page: the [id] effect re-runs after the new page renders; tick()
  //    may need a frame/timer before the target is in the DOM.
  //  - same-page: wouter calls history.pushState for hash-only changes, which
  //    it monkey-patches to dispatch a "pushState" event (plus native
  //    "hashchange"/"popstate"). Listening to those scrolls immediately,
  //    because the target is already in the DOM.
  useEffect(() => {
    let raf = 0
    let timer: number | undefined
    const tick = () => {
      const el = document.getElementById(window.location.hash.slice(1))
      if (el) {
        el.scrollIntoView({ block: 'start' })
        return true
      }
      return false
    }
    const jump = () => {
      if (!window.location.hash) return
      if (tick()) return
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        if (!tick()) timer = window.setTimeout(tick, 80)
      })
    }
    jump() // run on mount + whenever the page (id) changes, after render
    const events = ['pushState', 'replaceState', 'hashchange', 'popstate']
    for (const e of events) window.addEventListener(e, jump)
    return () => {
      cancelAnimationFrame(raf)
      if (timer) clearTimeout(timer)
      for (const e of events) window.removeEventListener(e, jump)
    }
  }, [id])

  const title = body?.name ?? faction?.name ?? 'Not found'
  const chapters: Chapter[] = body
    ? [{ id: 'overview', label: 'Overview' }, ...bodyChapters(body)]
    : faction
      ? [
          { id: 'overview', label: 'Overview' },
          ...(faction.sections?.map((s) => ({ id: s.id, label: s.title })) ?? []),
          ...(faction.subfactions?.length ? [{ id: 'subfactions', label: 'Subfactions' }] : []),
        ]
      : []

  return (
    <LorebookShell activeId={id} title={title} chapters={chapters}>
      {body ? (
        <>
          <header id="overview" className="scroll-mt-24">
            <div className="flex items-center gap-3">
              <span
                className="h-3.5 w-3.5 rounded-full"
                style={{ background: body.render.color, boxShadow: `0 0 16px ${body.render.color}` }}
              />
              <div className="text-[11px] uppercase tracking-[0.3em] text-signal/70">
                {typeLabel[body.type] ?? body.type}
              </div>
            </div>
            <h1 className="mt-2 font-display text-4xl font-semibold text-white">{body.name}</h1>
            {body.tagline && <p className="mt-1 text-slate-500">{body.tagline}</p>}
            <div className="md mt-6 text-slate-300">
              <Markdown>{body.lore}</Markdown>
            </div>
          </header>

          <BodyExtras body={body} variant="reader" />

          <div className="mt-10">
            <Link href="/" className="text-sm text-signal hover:underline">
              See {body.name} on the map →
            </Link>
          </div>
        </>
      ) : faction ? (
        <>
          <header id="overview" className="scroll-mt-24">
            <div className="text-[11px] uppercase tracking-[0.3em] text-thelxis/70">Faction</div>
            <h1 className="mt-2 font-display text-4xl font-semibold text-white">{faction.name}</h1>
            <p className="mt-1 text-slate-500">{faction.tagline}</p>
            {(faction.leader || faction.seat) && (
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-400">
                {faction.leader && (
                  <span>
                    Led by <span className="text-slate-200">{faction.leader}</span>
                  </span>
                )}
                {faction.seat && (
                  <span>
                    Seat: <span className="text-slate-200">{faction.seat}</span>
                  </span>
                )}
              </div>
            )}
            <div className="md mt-6 text-slate-300">
              <Markdown>{faction.lore}</Markdown>
            </div>
          </header>

          {faction.sections?.map((s) => (
            <LoreSection key={s.id} id={s.id} title={s.title}>
              <div className="md text-slate-300">
                <Markdown>{s.body}</Markdown>
              </div>
            </LoreSection>
          ))}

          {faction.subfactions?.length ? (
            <LoreSection id="subfactions" title="Subfactions">
              <div className="divide-y divide-edge">
                {faction.subfactions.map((s) => (
                  <div key={s.id} className="py-4 first:pt-0 last:pb-0">
                    <div className="font-display text-white">{s.name}</div>
                    {s.tagline && <div className="text-xs text-slate-400">{s.tagline}</div>}
                    <div className="md mt-2 text-sm">
                      <Markdown>{s.lore}</Markdown>
                    </div>
                  </div>
                ))}
              </div>
            </LoreSection>
          ) : null}
        </>
      ) : (
        <div className="py-24 text-center text-slate-500">
          No entry found for <span className="text-slate-300">"{id}"</span>.
          <div className="mt-3">
            <Link href="/lorebook" className="text-signal hover:underline">
              Back to the lorebook
            </Link>
          </div>
        </div>
      )}
    </LorebookShell>
  )
}
